import React from 'react'
import { Box, Icon, P, H4, H6, ListGrid, ListGridItem } from '@maui/elements'
import { useVulnerabilityQuery } from '@nautilusjs/mati-apis'
import { SearchPreviewHeader, ShowMoreDescription } from './common'
import { formattedDate, useMemoizedDescription } from '../common'
import RatingsPanel from './common/RatingsPanel'
import CvssPanel from './common/CvssPanel'

const ListGridElement = ({
  data,
  iconName,
  cellWidth = '50%',
}) => {
  return (
    <ListGrid list={data} cellWidth={[cellWidth]}>
      {(item) => (
        <Box>
          <ListGridItem>
            {iconName && <Box> <Icon name={iconName} /></Box>}
            <H6 wordBreak="break-all" m={0}>{item.name}</H6>
          </ListGridItem>
        </Box>
      )}
    </ListGrid>
  )
}

const Associations = ({ title, data, iconName, style }) => {
  if (!data?.length) return null
  return <Box
    display='flex'
    flexDirection='row'
    {...style}
  >
    <P width='25%' textStyle='bodySmall' m='0' fontWeight='700'>
      {title}
    </P>
    <Box width='75%'>
      <ListGridElement
        data={data}
        iconName={iconName}
      />
    </Box>
  </Box>
}

export const CveSearchPreview = ({ data }) => {
  const {
    id,
    description,
    common_vulnerability_scores: vulnScore = {},
    cve_id: name,
    date_of_disclosure: date,
    executive_summary: executiveSummary,
    risk_rating: riskRating,
    exploitation_state: exploitationState,
    was_zero_day: zeroDay,
    observed_in_the_wild: inTheWild,
  } = data
  const formattedExecutedSummary = useMemoizedDescription(executiveSummary)
  const cveDescription = formattedExecutedSummary || description
  const ratings = (riskRating && exploitationState) && { riskRating, exploitationState }
  const { data: cve = {} } = useVulnerabilityQuery(id)
  const {
    associated_actors: associatedActors = [],
    associated_malware: associatedMalware = [],
    associated_tools: associatedTools = []
  } = cve
  const showAssociations = associatedActors.length > 0 || associatedMalware.length > 0 || associatedTools.length > 0

  return (
    <Box>
      <SearchPreviewHeader
        name={name}
        icon={<Icon name='shield-alert' size='xl' marginTop='-8px' />}
      />
      <Box p='16px' background='colorBackgroundPage' color='colorTextMain'>
        <Box>
          <H4 m={0}>Summary</H4>
        </Box>
        <Box pt='16px'>
          <ShowMoreDescription description={cveDescription} />
        </Box>
        <Box pt='16px'>
          <Box display='flex' flexDirection='row' marginBottom='10px'>
            <Box width='25%'>
              <Box fontSize='12px' fontWeight={400} lineHeight='16px' color='colorTextDeemphasis'>
                Disclosed
              </Box>
            </Box>
            <Box width='75%'>
              <H6 m={0}>{formattedDate(date)}</H6>
            </Box>
          </Box>
        </Box>
      </Box>
      {ratings &&
        <Box pt='16px'>
          <Box p='16px' background='colorBackgroundPage' color='colorTextMain'>
            <Box>
              <H4 m={0}>Severity</H4>
            </Box>
            <Box pt='16px'>
              <RatingsPanel
                ratings={ratings}
                zeroDay={zeroDay}
                inTheWild={inTheWild}
                variant='priorityLabel'
              />
            </Box>
          </Box>
        </Box>
      }
      {showAssociations &&
        <Box pt='16px'>
          <Box p='16px' background='colorBackgroundPage' color='colorTextMain'>
            <Box>
              <H4 m={0}>Associations</H4>
            </Box>
            <Box pt='16px'>
              <Associations
                title='Actors'
                data={associatedActors}
                iconName='user-secret'
              />
              <Associations
                title='Malware'
                data={associatedMalware}
                iconName='bug'
                style={{
                  marginTop: associatedActors.length > 0 ? '12px' : '0'
                }}
              />
              <Associations
                title='Tools'
                data={associatedTools}
                iconName='tools'
                styles={{
                  marginTop: associatedActors.length > 0 || associatedMalware.length > 0 ? '12px' : '0'
                }}
              />
            </Box>
          </Box>
        </Box>
      }
      {Object.keys(vulnScore).length > 0 &&
        <Box pt='16px'>
          <Box p='16px' background='colorBackgroundPage' color='colorTextMain'>
            <Box>
              <H4 m={0}>CVSS Ratings</H4>
            </Box>
            <Box>
              <CvssPanel vulnScore={vulnScore} />
            </Box>
          </Box>
        </Box>
      }
    </Box>
  )
}
